// used to filter and generate only the minimum necessary set of module fragments
// (dynamically determined at run-time based on top-level page modules present)

// see moduleFragmentsMap after fragment definitions at bottom of file.
export function getModuleFragments(typenames: string[]): string {
  return typenames
    .map(type => moduleFragmentsMap[type])
    .filter(Boolean) // skip any undefined
    .join('\n');
}

/**
 * Inline components
 */

export const themedTextFragment = `
  ...SysId
  themedText {
    json
  }
`;

export const documentWithCommonEntriesFragment = `
  json
  links {
    entries {
      inline {
        ...CommonInlineFields
      }
    }
  }
`;

export const unlockedTextFragment = `
  ...SysId
  unlockedText {
    ${documentWithCommonEntriesFragment}
  }
`;

export const youTubeEmbedFragment = `
  ...SysId
  youTubeVideoUrl
`;

export const boxTableFragment = `
  ...SysId
  tableHeader
  tableBody {
    ${documentWithCommonEntriesFragment}
  }
`;

/**
 * Inner components
 */

export const internalPageFragment = `
  ...InternalPageRef
`;

export const hyperlinkFragment = `
  hyperlink {
    __typename
    ...SysId
    ... on Page {
      slug
    }
  }
`;

export const assetFragment = `
  block {
    sys {
      id
    }
    url
    title
    width
    height
    description
    contentType
  }
`;

export const disclosureFragment = `
  ...DisclosureFull
`;

export const documentWithHyperlinkEntriesFragment = `
  json
  links {
    entries {
      ${hyperlinkFragment}
      inline {
        ...CommonInlineFields
      }
    }
  }
`;

export const disclosureItemFragment = `
  ...DisclosureItemFields
`;

export const disclosureSymbolFragment = `
  ...DisclosureSymbolFields
`;

const inlineEntriesDisclosureOnlyFragment = `
  inline {
    __typename
    ... on Disclosure {
      ${disclosureFragment}
    }
  }
`;

export const documentWithDisclosureEntriesOnlyFragment = `
  json
  links {
    entries {
      ${inlineEntriesDisclosureOnlyFragment}
    }
  }
`;

export const redirectFragment = `
  sys {id}
  source
  destination
  permanent
  redirectType
  isEnterprise
`;

export const inlineEntriesRTEFragment = `
  inline {
    __typename
    ... on YouTubeEmbed {
      ${youTubeEmbedFragment}
    }
    ... on BoxTable {
      ${boxTableFragment}
    }
    ... on Disclosure {
      ${disclosureFragment}
    }
    ... on Shortcode {
      ...ShortcodeFields
    }
    ... on Linebreak {
      ...LinebreakFields
    }
    ... on Modal {
      ...ModalFields
    }
  }
`;

const inlineEntriesTextFormattingFragment = `
  inline {
    __typename
    ... on UnlockedText {
      ...SysId
      unlockedText {
        json
      }
    }
    ... on ThemedText {
      ${themedTextFragment}
    }
    ...CommonInlineFields
  }
`;

export const documentWithInlineEntriesTextFragment = `
  json
  links {
    entries {
      ${inlineEntriesTextFormattingFragment}
    }
  }
`;

export const documentWithHyperlinkAndAllInlineTextEntriesFragment = `
  json
  links {
    entries {
      ${hyperlinkFragment}
      ${inlineEntriesTextFormattingFragment}
    }
  }
`;

export const documentWithHyperlinkOrRTEInlineEntriesFragment = `
  json
  links {
    entries {
      ${hyperlinkFragment}
      ${inlineEntriesRTEFragment}
    }
    assets {
      ${assetFragment}
    }
  }
`;

export const mediaFragment = `
  title
  description
  contentType
  fileName
  size
  url
  width
  height
`;

export const aspectRatioFragment = `
  value
`;

export const eyebrowFragment = `
  ...SysId
  showChimeLogo
  textRte {
    ${documentWithCommonEntriesFragment}
  }
  alternateLogo {
    ${mediaFragment}
  }
`;

export const ctaFragment = `
  ...SysId
  text
  url
  internalPage {
    ...InternalPageRef
  }
  variant
`;

export const logoFragment = `
  ...SysId
  image {
    ${mediaFragment}
  }
`;

export const modalFragment = `
  ...SysId
  copy {
    ${documentWithHyperlinkEntriesFragment}
  }
  headline
  disclaimerText
`;

export const hyperlinkedMediaFragment = `
  link
  image {
    ${mediaFragment}
  }
`;

export const bentoBoxCardFragment = `
  ...SysId
  icon: cardIcon {
    ${mediaFragment}
  }
  aspectRatio {${aspectRatioFragment}}
  copy: cardCopy {
    ${documentWithHyperlinkEntriesFragment}
  }
  cardHeadlineRte {
    ${documentWithCommonEntriesFragment}
  }
  color: cardBackgroundColor
`;

export const bentoBoxAppReviewCardFragment = `
  ...SysId
  copy {
    ${documentWithHyperlinkEntriesFragment}
  }
  cardHeadlineRte {
    ${documentWithCommonEntriesFragment}
  }
  color
  numberOfStars
`;

export const iconBulletFragment = `
  iconName
  icon {
    ${mediaFragment}
  }
  copy {
    ${documentWithCommonEntriesFragment}
  }
`;

export const cardsExpandableItemFragment = `
  __typename
  ...SysId
  eyebrow {
    ${documentWithInlineEntriesTextFragment}
  }
  cardHeadline {
    ${documentWithInlineEntriesTextFragment}
  }
  cardCopy {
    ${documentWithInlineEntriesTextFragment}
  }
  cardImage {
    ${mediaFragment}
  }
  aspectRatio {${aspectRatioFragment}}
  cardListItems: iconListCollection(limit: 5) {
    items {
       ${iconBulletFragment}
    }
  }
`;

export const crossSellFragment = `
  __typename
  ...SysId
  title {
    ${documentWithCommonEntriesFragment}
  }
  copy {
    ${documentWithHyperlinkAndAllInlineTextEntriesFragment}
  }
  media {
    ${mediaFragment}
  }
  cta {
    ${ctaFragment}
  }
  color
`;

/**
 * Base fragment with common fields shared across components
 */
export const baseComponentFragment = `
  __typename
  ...SysId
  schema
  sectionName
`;

/**
 * Top level components
 */

export const accordionFragment = `
  ${baseComponentFragment}
  headline {
    ${documentWithCommonEntriesFragment}
  }
  copy {
    ${documentWithHyperlinkEntriesFragment}
  }
  color
  margins
  media {
    ${mediaFragment}
  }
  aspectRatio {${aspectRatioFragment}}
  accordionItems: contentCollection(limit: 10) {
    items {
      sectionName
      ...SysId
      accordionCopy {
        ${documentWithDisclosureEntriesOnlyFragment}
      }
      accordionLabelRte {
        ${documentWithCommonEntriesFragment}
      }
    }
  }
`;

export const disclosureGroupingsFragment = `
  ${baseComponentFragment}
  disclosures: disclosuresCollection(limit: 25) {
    items {
      ...DisclosureFull
    }
  }
  showKeyline
  margins
`;

export const sectionDisclosuresLightweightFragment = `
  ...SysId
  disclosuresCollection(limit: 5) {
    items {
      __typename
      ... on DisclosureGroupings {
        ...SysId
      }
      ... on DisclosureBanner {
        ...SysId
      }
    }
  }
`;

export const faqFragment = `
  ${baseComponentFragment}
  headline {
    ${documentWithCommonEntriesFragment}
  }
  color
  toggleItems: contentCollection(limit: 3) {
    items {
      ...SysId
      tabCopy
      contentCollection(limit: 25) {
        items {
          ...SysId
          sectionName
          accordionCopy {
            ${documentWithHyperlinkEntriesFragment}
          }
          accordionLabelRte {
            ${documentWithCommonEntriesFragment}
          }
        }
      }
    }
  }
`;

export const linkedIconFragment = `
  ...LinkedIconFields
`;

export const ctaAddOnFragment = `
  ${baseComponentFragment}
  keyLine
  headline {
    ${documentWithCommonEntriesFragment}
  }
  cta {
    __typename
    ... on Cta {
      ${ctaFragment}
    }
    ... on LinkedIcon {
      ${linkedIconFragment}
    }
  }
  color
  margins
  variant
  alignment
  transparentBackground
  pinCtaAboveFold
`;

export const linkFragment = `
  ...LinkFields
`;

export const socialMediaLinkFragment = `
  ...SocialMediaLinkFields
`;

export const footerColumnFragment = `
  heading
  linksCollection(limit: 9) {
    items {
      __typename
      ... on Link {
        ${linkFragment}
      }
      ... on LinkedIcon {
        ${linkedIconFragment}
      }
    }
  }
`;

export const footerFragment = `
  __typename
  ...SysId
  showSocialLinkColumn
  footerColumns: footerColumnsCollection(limit: 5) {
    items {
      ${footerColumnFragment}
    }
  }
`;

export const sectionFooterLightweightFragment = `
  ...SysId
  footer {
    __typename
    ... on SiteFooter {
      ...SysId
    }
  }
`;

export const pageVisualSettingsFragment = `
  ...SysId
  pageColor
  footerColor
  showFooterKeyline
  transparentNav
  moduleThresholdNavColorChange
`;

export const sectionPageVisualSettingsFragment = `
  ...SysId
  pageVisualSettings {
    __typename
    ${pageVisualSettingsFragment}
  }
`;

export const sectionBannerLightweightFragment = `
  ...SysId
  headerBannerCollection {
    items {
      __typename
      ... on HeaderLogo {
        ...SysId
      }
      ... on FeaturedBanner {
        ...SysId
      }
    }
  }
`;

// headlineEyebrow reference field to be deprecated
export const mediaCopyFragment = `
  ${baseComponentFragment}
  headlineEyebrow {
    ${eyebrowFragment}
  }
  eyebrowText {
    ${documentWithCommonEntriesFragment}
  }
  eyebrowAlternateLogo {${mediaFragment}}
  eyebrowChimeLogo: showChimeLogoInEyebrow
  headline {
    ${documentWithCommonEntriesFragment}
  }
  alignment
  mobileAlignment
  variant
  copy {
    ${documentWithHyperlinkEntriesFragment}
  }
  sliderEntryCollection: sliderEntriesCollection (limit: 8) {
    items {
      __typename
      content{ ${documentWithHyperlinkEntriesFragment} }
    }
  }
  copyPosition
  color
  containedColor
  margins
  mobileCopyJustification
  cta {
    ...CtaFields
  }
  hyperlinkedImagesCollection(limit: 2) {
    items {
      ${hyperlinkedMediaFragment}
    }
  }
  notificationOverlay {
    ...SysId
    display
    image {
      ...MediaFields
    }
  }
  notificationOverlayDesktopPosition
  image {
    sys {id}
    ...MediaFields
  }
  aspectRatio {${aspectRatioFragment}}
  topRightBadge {
    ...SysId
    image {
      ...MediaFields
    }
  }
`;

export const calloutIconFragment = `
  ${baseComponentFragment}
  keyLine
  headline {
    ${documentWithInlineEntriesTextFragment}
  }
  columns
  content: contentCollection (limit: 8) {
    items{
      icon{
        ${mediaFragment}
      }
      headline {
        ${documentWithCommonEntriesFragment}
      }
      headlineSmallRte {
        ${documentWithCommonEntriesFragment}
      }
      copy {
        ${documentWithHyperlinkOrRTEInlineEntriesFragment}
      }
      attributionRte {
        ${documentWithCommonEntriesFragment}
      }
      link {
        ${linkFragment}
      }
    }
  }
  color
  margins
  copyJustification
`;

export const calloutGridFragment = `
  __typename
  ...SysId
  schema
  sectionName
  keyline
  columns
  variant
  cards: cardsCollection (limit: 10) {
    items {
      __typename
      ... on CalloutGridCard {
        ...SysId
        icon
        image{
          ${mediaFragment}
        }
        aspectRatio {
          value
        }
        headlineSmallRte{
          ${documentWithInlineEntriesTextFragment}
        }
        headline {
          ${documentWithInlineEntriesTextFragment}
        }
        copy {
          ${documentWithHyperlinkEntriesFragment}
        }
        link {...LinkFields}
        attributionRte {${documentWithInlineEntriesTextFragment}}
      }
    }
  }
  color
  margins
  copyJustification
`;

export const testimonialItemFragment = `
  __typename
  ...SysId
  media {
    ${mediaFragment}
  }
  aspectRatio {${aspectRatioFragment}}
  copy {
    ${documentWithHyperlinkEntriesFragment}
    links {
      entries {
        inline {
          __typename
          ... on ThemedText {
            ${themedTextFragment}
          }
        }
      }
    }
  }
  cta {
    ${linkFragment}
  }
  attributionRte {
    ${documentWithCommonEntriesFragment}
  }
`;

export const testimonialFragment = `
  ${baseComponentFragment}
  variant
  sectionColor
  testimonialItems: testimonialItemsCollection(limit: 5) {
    items {
      ${testimonialItemFragment}
    }
  }
  disclaimer {
    ${documentWithCommonEntriesFragment}
  }
  margins
  slideColor
`;

export const calloutImageFragment = `
  ...SysId
  __typename
  sectionName
  keyLine
  headline {
    ${documentWithInlineEntriesTextFragment}
  }
  columns
  content: contentCollection(limit: 6){
    items{
      image{
        ${mediaFragment}
      }
      headline {
        ${documentWithHyperlinkEntriesFragment}
      }
      headlineSmallRte {
        ${documentWithCommonEntriesFragment}
      }
      copy {
        ${documentWithHyperlinkEntriesFragment}
      }
      copyJustification
      attributionRte {
        ${documentWithCommonEntriesFragment}
      }
    }
  }
  color
  margins
`;

export const impactHeadlineFragment = `
  ${baseComponentFragment}
  prebuiltHeadline
  richTextHeadline {
    ${documentWithInlineEntriesTextFragment}
  }
  copy {
    ${documentWithHyperlinkEntriesFragment}
  }
  color
  margins
  ctas: ctaCollection(limit: 2) {
    items {
      ${ctaFragment}
    }
  }
  showEmailInput
  emailInputButtonText
`;

export const subNavFragment = `
  ${baseComponentFragment}
  linksCollection(limit: 7) {
    items {
      url
      text
    }
  }
`;

export const cardsExpandableFragment = `
  ${baseComponentFragment}
  name
  margins
  color
  cardsExpandableItems: contentCollection(
    limit: 2
  ) {
    items {
      __typename
      ... on CardsExpandableItem {
        ${cardsExpandableItemFragment}
      }
    }
  }
`;

export const cardsGridComplexItemFragment = `
  ...SysId
  __typename
  sectionName
  desktopImage {
    ${mediaFragment}
  }
  mobileImage {
    ${mediaFragment}
  }
  eyebrow
  eyebrowRte {
    ${documentWithCommonEntriesFragment}
  }
  headline {
   ${documentWithCommonEntriesFragment}
  }
  copy {
    ${documentWithCommonEntriesFragment}
  }
  cta {
    ${ctaFragment}
  }
  color
  topRightBadge {
    ...SysId
    image {
      ${mediaFragment}
    }
  }
`;

export const cardsIconItemFragment = `
  name
  cardIcon {
    url
    description
    size
  }
  cardHeadlineRte {
    ${documentWithCommonEntriesFragment}
  }
  cardCopy {
    ${documentWithHyperlinkOrRTEInlineEntriesFragment}
  }
  color
  jumpToCta {
    text
    url
  }
  showFiveStarEyebrow
  textEyebrowRte {
    ${documentWithCommonEntriesFragment}
  }
  hyperlinkedImagesCollection(limit: 2) {
    items {
      ${hyperlinkedMediaFragment}
    }
  }
  imageLayout
`;

export const cardsIconFragment = `
  ${baseComponentFragment}
  name
  cardStyle
  color
  margins
  cardIconItems: contentCollection(
    limit: 6
  ) {
    items {
      __typename
      ... on CardsIconItem {
        ${cardsIconItemFragment}
      }
    }
  }
`;

export const cardsImageItemFragment = `
  __typename
  name
  cardImage {
  ${mediaFragment}
  }
  imageWidth
  cardHeadlineRte {
    ${documentWithCommonEntriesFragment}
  }
  cardCopy {
    ${documentWithHyperlinkEntriesFragment}
  }
  color
  hyperlinkedImagesCollection(limit: 2) {
    items {
      ${hyperlinkedMediaFragment}
    }
  }
  imageLayout
`;

export const cardsImageFragment = `
  ${baseComponentFragment}
  name
  cardStyle
  color
  margins
  cardImageItems: contentCollection(
    limit: 6
  ) {
    items {
      ${cardsImageItemFragment}
    }
  }
`;

export const cardsGridComplexFragment = `
  ${baseComponentFragment}
  margins
  color
  cardLayout
  cards: cardsCollection(
    limit: 6
  ) {
    items {
      ${cardsGridComplexItemFragment}
    }
  }
`;

export const cardsStackedItemFragment = `
  ...SysId
  __typename
  sectionName
  desktopMedia {
    ${mediaFragment}
  }
  desktopAspectRatio {${aspectRatioFragment}}
  mobileMedia {
    ${mediaFragment}
  }
  mobileAspectRatio {${aspectRatioFragment}}
  eyebrow {
    ${documentWithCommonEntriesFragment}
  }
  headline {
    ${documentWithCommonEntriesFragment}
  }
  shortDescription {
    ${documentWithHyperlinkAndAllInlineTextEntriesFragment}
  }
  cta {
    ${ctaFragment}
  }
  color
  iconAsset: icon {
    ...MediaFields
  }
  chimeKitIllustrationToken
  topRightBadge {
    ...SysId
    image {
      ${mediaFragment}
    }
  }
  badgeOverlayAlignment
  copyAlignment
  bulletStyle
  desktopAnimation
  mobileAnimation
`;

export const cardsStackedFragment = `
  ${baseComponentFragment}
  variant
  margins
  color
  cardLayout
  disclosure {
    ${disclosureFragment}
  }
  cards: cardsCollection(
    limit: 6
  ) {
    items {
      ${cardsStackedItemFragment}
    }
  }
`;

export const videoItemFragment = `
  __typename
  ...SysId
  video {
    ${mediaFragment}
  }
  videoUrl
  previewImage {
    ${mediaFragment}
  }
  overlayLabel
  aspectRatio {aspectRatio}
`;

export const richTextEditorFragment = `
  ...SysId
  __typename
  sectionName
  content {
    ${documentWithHyperlinkOrRTEInlineEntriesFragment}
  }
`;

export const hubSpotFragment = `
  ${baseComponentFragment}
  headline {
    ${documentWithCommonEntriesFragment}
  }
  formId
`;

export const bentoBoxFragment = `
  ${baseComponentFragment}
  headline {
    ${documentWithInlineEntriesTextFragment}
  }
  copy {
    ${documentWithHyperlinkEntriesFragment}
  }
  eyebrowRte {
    ${documentWithCommonEntriesFragment}
  }
  largeCard {
    ...SysId
    copy: cardCopy {
      ${documentWithHyperlinkEntriesFragment}
    }
    headlineRte: cardHeadlineRte {
      ${documentWithCommonEntriesFragment}
    }
    color: cardBackgroundColor
  }
  cards: cardsCollection(limit: 4) {
    items {
    __typename
      ... on BentoBoxCard {
        ${bentoBoxCardFragment}
      }
      ... on BentoBoxAppReviewCard {
        ${bentoBoxAppReviewCardFragment}
      }
    }
  }
  color
  margins
  showFiveStarEyebrow
`;

export const compareFragment = `
  ${baseComponentFragment}
  color
  margins
  content {
    json
    links {
      __typename
      entries {
        inline {
          __typename
          ...SysId
          ... on Cta {
            ${ctaFragment}
          }
          ... on Logo {
            ${logoFragment}
          }
          ... on Disclosure {
            ${disclosureFragment}
          }
          ... on Shortcode {
            ...ShortcodeFields
          }
          ... on Linebreak {
            ...LinebreakFields
          }
        }
      }
    }
  }
`;

export const resourceFragment = `
  ...SysId
  __typename
  resourceIcon {
    ${mediaFragment}
  }
  aspectRatio {${aspectRatioFragment}}
  resourceLabelRte {
    ${documentWithCommonEntriesFragment}
  }
  resourceCopy {
    ${documentWithHyperlinkEntriesFragment}
  }
  resourcePage {
    ${internalPageFragment}
  }
  url
`;

export const resourcesItgBrandedFragment = `
  ${baseComponentFragment}
  headline {
    ${documentWithCommonEntriesFragment}
  }
  cta {
    ${ctaFragment}
  }
  keyline
  resources: contentCollection(limit: 9) {
    items {
      ${resourceFragment}
    }
  }
  color
  margins
`;

export const mainNavFragment = `
  ...SysId
  __typename
  variant
  cta {
    ...CtaFields
  }
  topBarLink: topNavBarSecondaryLink {
    ${linkedIconFragment}
  }
  navContent: topLevelLinksCollection(limit:12) {
    items {
      link {
        ${linkFragment}
      }
      linkSize
      subMenu {
        ...SysId
        titleOverride
        mainDescription
        linkLabel
        sublinksCollection(limit:10) {
          items {
            ...SysId
            link {
              ${linkFragment}
            }
            description
            chimeKitIconName
            icon {
              ...MediaFields
            }
          }
        }
      }
    }
  }
  resourcesLabel
  resourcesUrl
  resourcesInternalPage {${internalPageFragment}}
  resourcesCollection(limit:4) {
    items {
      ${resourceFragment}
    }
  }
`;

export const featuredBannerFragment = `
  ...SysId
  __typename
  text
  pinned
  color
  cta {
    url
    label
    target
    id
    class
  }
  ctaAsButton
  showCloseButton
`;

export const responsiveMediaFragment = `
  ...SysId
  desktopImage {
    ${mediaFragment}
  }
  mobileImage {
    ${mediaFragment}
  }
`;

export const featuresFragment = `
  ...SysId
  __typename
  title
  color
  transparentBackground
  fullWidth
  mobileOrientation
  keyline
  content: contentCollection(limit: 6) {
    items {
      ... on ResponsiveMedia {
        ${responsiveMediaFragment}
      }
    }
  }
`;

export const disclosureBannerFragment = `
  ...SysId
  __typename
  copy {
    ${documentWithCommonEntriesFragment}
  }
`;

export const cardsGridSimpleItemFragment = `
  ...SysId
  sectionName
  icon {
    ${mediaFragment}
  }
  headline {
    ${documentWithCommonEntriesFragment}
  }
  copy {
    ${documentWithCommonEntriesFragment}
  }
  cta {
    ${ctaFragment}
  }
  color
`;

export const cardsGridSimpleFragment = `
  ...SysId
  __typename
  color
  cards: cardsCollection(limit: 6) {
    items {
      ${cardsGridSimpleItemFragment}
    }
  }
`;

export const noteFragment = `
  ...SysId
  __typename
  color
  margins
  headline {
    ${documentWithHyperlinkEntriesFragment}
  }
  copy {
    ${documentWithHyperlinkEntriesFragment}
  }
`;

export const launchpadFragment = `
  ${baseComponentFragment}
  headline {
    ${documentWithInlineEntriesTextFragment}
  }
  copy {
    ${documentWithHyperlinkEntriesFragment}
  }
  cta {
    ${ctaFragment}
  }
  outerColor
  innerColor
  margins
`;

export const heroConsolidatedFragment = `
...HeroFields
`;

export const heroCarouselFragment = `
  ...SysId
  slides: slidesCollection(limit: 7) { items {${heroConsolidatedFragment}}}
  sectionName
  color
  mobileMediaLocation
  margins
  scrollCta { ...CtaFields }
`;

export const homepageHeroFragment = `
  ...SysId
  __typename
  sectionName
  variant
  headline {
    ${documentWithHyperlinkOrRTEInlineEntriesFragment}
  }
  copy {
    ${documentWithHyperlinkEntriesFragment}
  }
  desktopImage {${mediaFragment}}
  desktopAspectRatio {${aspectRatioFragment}}
  mobileImage {${mediaFragment}}
  mobileAspectRatio {${aspectRatioFragment}}
  images: imagesCollection {
    items {
      ${mediaFragment}
    }
  }
  color
  transparentBackground
  showEmailInput
  emailInputButtonText
  disclaimers {
    ${documentWithHyperlinkEntriesFragment}
  }
`;

export const sectionHeroFragment = `
  ...SysId
  hero {
    __typename
    ... on HeroConsolidated {
      ${heroConsolidatedFragment}
    }
    ... on HeroCarousel {
      ${heroCarouselFragment}
    }
    ... on HomepageHero {
      ${homepageHeroFragment}
    }
  }
`;

export const sectionNavigationLightweightFragment = `
  ...SysId
  nav {
    __typename
    ... on Nav {
      ...SysId
    }
    ... on HeaderCta {
      ...SysId
    }
  }
`;

export const sectionHeroLightweightFragment = `
  ...SysId
  hero {
    __typename
    ... on HeroConsolidated {
      ...SysId
    }
    ... on HeroCarousel {
      ...SysId
    }
    ... on HomepageHero {
      ...SysId
    }
  }
`;

export const animatedMarqueeFragment = `
  ...SysId
  __typename
  color
  copy {
    ${documentWithCommonEntriesFragment}
  }
  cta {
    ${ctaFragment}
  }
  ctaStyle
`;

export const headerLogoFragment = `
  ...SysId
  __typename
  sectionName
  pinned
  color
  headerLogoItems: itemsCollection(limit: 3) {
    items {
      variant
      icon
      illustration
      image {${mediaFragment}}
      aspectRatio {
        value
      }
    }
  }
 `;

export const mapLocatorFragment = `
  ...SysId
  __typename
  sectionName
  color
  inputPlaceholder
  headline {
    ${documentWithInlineEntriesTextFragment}
  }
  copy {
    ${documentWithHyperlinkEntriesFragment}
  }
  margins
`;

export const teaserlinksFragment = `
  ${baseComponentFragment}
  color
  headline {
    ${documentWithCommonEntriesFragment}
  }
  margins
  keyline
  layout
  linksCollection(limit: 6) {
    items {
      linkText
      internalPage {${internalPageFragment}}
      url
      image {
        ${mediaFragment}
      }
    }
  }
`;

export const authorFragment = `
  sys {id}
  name
  image {${mediaFragment}}
  title
  url
  internalPage {${internalPageFragment}}
`;

export const statsigExperimentFragment = `
  __typename
  sys {id}
  entryName
  experimentId
  controlVariation {
    sys {id}
    ... on HeroConsolidated {
      ${heroConsolidatedFragment}
    }
  }
  treatmentVariationsCollection {
    items {
      sys {id}
      ... on HeroConsolidated {
        ${heroConsolidatedFragment}
      }
    }
  }
`;

export const relatedArticleFragment = `
  __typename
  ...SysId
  articleTitle {
    json
    links {
      entries {
        inline {
          __typename
          ... on Shortcode {
            ...ShortcodeFields
          }
          ... on Linebreak {
            ...LinebreakFields
          }
          ... on Modal {
            ...ModalFields
          }
        }
      }
    }
  }
  articleImage {${mediaFragment}}
  slug
  categories: contentfulMetadata {
    items: concepts {
      id
    }
  }
  category {${linkFragment}}
`;

export const relatedArticleFlexibleFragment = `
  __typename
  ...SysId
  articleTitle: hero {
    ... on HeroConsolidated {
      headline {
        json
        links {
          entries {
            inline {
              __typename
              ... on Shortcode {
                ...ShortcodeFields
              }
              ... on Linebreak {
                ...LinebreakFields
              }
              ... on Modal {
                ...ModalFields
              }
            }
          }
        }
      }
    }
  }
  articleImage: hero {
    ...on HeroConsolidated {
      desktopMedia {
        ...MediaFields
      }
    }
  }
  slug
  categories: contentfulMetadata {
    items: concepts {
      id
    }
  }
  category {${linkFragment}}
`;

export const headerCtaFragment = `
  __typename
  ... SysId
  cta {${ctaFragment}}
  color
`;

export const sectionNavigationFragment = `
  ...SysId
  nav {
    __typename
    ... on Nav {
      ${mainNavFragment}
    }
    ... on HeaderCta {
      ${headerCtaFragment}
    }
  }
`;

export const ContentBlockFragment = `
  sys {id}
  variant
  sectionName
  margins
  color
  eyebrow {
    ${documentWithHyperlinkOrRTEInlineEntriesFragment}
  }
  eyebrowAlternateLogo {${mediaFragment}}
  eyebrowChimeLogo
  headline {
    ${documentWithHyperlinkOrRTEInlineEntriesFragment}
  }
  copy {
    ${documentWithHyperlinkOrRTEInlineEntriesFragment}
  }
  cta {
    ${ctaFragment}
  }
  socialMedia:socialMediaCollection (limit: 4) {
    items {
      ${socialMediaLinkFragment}
    }
  }
  alignment
`;

export const cardFragment = `
  sys {id}
  sectionName
  variant
  color
  showFiveStarEyebrow
  showChimeLogo
  eyebrow {${documentWithInlineEntriesTextFragment}}
  headline {${documentWithInlineEntriesTextFragment}}
  copy {${documentWithHyperlinkAndAllInlineTextEntriesFragment}}
  iconImage {${mediaFragment}}

  desktopMedia {${mediaFragment}}
  desktopAspectRatio {${aspectRatioFragment}}
  mobileMedia {${mediaFragment}}
  mobileAspectRatio {${aspectRatioFragment}}

  imageSize
  hyperlinkedImages:hyperlinkedImagesCollection(limit: 2) {
    items {
      ${hyperlinkedMediaFragment}
    }
  }
  hyperlinkedImagesLocation
  cta {${ctaFragment}}
`;

export const cardCarouselFragment = `
  sys {id}
  sectionName
  color
  margins
  cardStyle
  cards:cardsCollection(limit: 20) {
    items {
      ${cardFragment}
    }
  }
`;

export const contentFeatureCarousel = `
  ...SysId
  sectionName
  color
  margins
  eyebrow {${documentWithInlineEntriesTextFragment}}
  headline {${documentWithInlineEntriesTextFragment}}
  copy {${documentWithHyperlinkEntriesFragment}}
  cta {${ctaFragment}}
  cardCarousel {${cardCarouselFragment}}
`;

const cardsVerticalScrollItem = `
  ...SysId
  sectionName
  backgroundColor
  headline {${documentWithInlineEntriesTextFragment}}
  copy {${documentWithHyperlinkAndAllInlineTextEntriesFragment}}
  desktopMedia {${mediaFragment}}
  mobileMedia {${mediaFragment}}
  desktopAspectRatio {${aspectRatioFragment}}
  mobileAspectRatio {${aspectRatioFragment}}
  cta {${ctaFragment}}
  bulletStyle
`;

const cardsVerticalScroll = `
  ${baseComponentFragment}
  margins
  transparentBackground
  cards:cardsVerticalScrollItemCollection (limit: 20) {
    items {
      ${cardsVerticalScrollItem}
    }
  }
  `;

export const fullBleedImageHeadlineFragment = `
  ${baseComponentFragment}
  headline {${documentWithCommonEntriesFragment}}
  eyebrow {${documentWithCommonEntriesFragment}}
  margins
  media {${mediaFragment}}
  mobileMedia {${mediaFragment}}
  aspectRatio {${aspectRatioFragment}}
  mobileAspectRatio {${aspectRatioFragment}}
`;

export const headlineWithEyebrowFragment = `
  ...SysId
  sectionName
  headline {${documentWithInlineEntriesTextFragment}}
  eyebrowLogo {${mediaFragment}}
  color
  schema
  margins
`;

export const articleGridFragment = `
  ${baseComponentFragment}
  variant
  margins
  color
  featuredArticleColor
  articlesCollection(limit: 20) {
    items { 
    ... on BlogPage {${relatedArticleFragment}}
    ... on Resource {${resourceFragment}}
    ... on PageBlogPostFlexible {${relatedArticleFlexibleFragment}}
    }
  }
  headline {${documentWithCommonEntriesFragment}}
  cta {${ctaFragment}}
`;

const moduleFragmentsMap: Record<string, string> = {
  Accordion: `... on Accordion { ${accordionFragment} }`,
  AnimatedMarquee: `... on AnimatedMarquee { ${animatedMarqueeFragment} }`,
  ArticleGrid: `... on ArticleGrid {${articleGridFragment}}`,
  BentoBox: `... on BentoBox { ${bentoBoxFragment} }`,
  CalloutIcon: `... on CalloutIcon { ${calloutIconFragment} }`,
  CalloutImage: `... on CalloutImage { ${calloutImageFragment} }`,
  CalloutGrid: `... on CalloutGrid { ${calloutGridFragment} }`,
  CardCarousel: `... on CardCarousel { ${cardCarouselFragment} }`,
  CardsExpandable: `... on CardsExpandable { ${cardsExpandableFragment} }`,
  CardsGridComplex: `... on CardsGridComplex { ${cardsGridComplexFragment} }`,
  CardsGridSimple: `... on CardsGridSimple { ${cardsGridSimpleFragment} }`,
  CardsIcon: `... on CardsIcon { ${cardsIconFragment} }`,
  CardsImage: `... on CardsImage { ${cardsImageFragment} }`,
  CardsStacked: `... on CardsStacked { ${cardsStackedFragment} }`,
  CardsVerticalScroll: `... on CardsVerticalScroll { ${cardsVerticalScroll} }`,
  Compare: `... on Compare { ${compareFragment} }`,
  CopyAddOnWithButton: `... on CopyAddOnWithButton { ${ctaAddOnFragment} }`,
  ContentBlock: `...on ContentBlock { ${ContentBlockFragment} }`,
  ContentFeatureCarousel: `...on ContentFeatureCarousel { ${contentFeatureCarousel} }`,
  DisclosureBanner: `... on DisclosureBanner { ${disclosureBannerFragment} }`,
  DisclosureGroupings: `... on DisclosureGroupings { ${disclosureGroupingsFragment} }`,
  SectionDisclosures: `... on SectionDisclosures { ${sectionDisclosuresLightweightFragment} }`,
  Faq: `... on Faq { ${faqFragment} }`,
  FeaturedBanner: `... on FeaturedBanner { ${featuredBannerFragment} }`,
  Features: `... on Features { ${featuresFragment} }`,
  FullBleedImageHeadline: `... on FullBleedImageHeadline { ${fullBleedImageHeadlineFragment} }`,
  HeaderCta: `... on HeaderCta { ${headerCtaFragment} }`,
  HeaderLogo: `... on HeaderLogo { ${headerLogoFragment} }`,
  HeadlineWithEyebrow: `... on HeadlineWithEyebrow { ${headlineWithEyebrowFragment} }`,
  HeroCarousel: `... on HeroCarousel { ${heroCarouselFragment} }`,
  HeroConsolidated: `... on HeroConsolidated { ${heroConsolidatedFragment} }`,
  HomepageHero: `... on HomepageHero { ${homepageHeroFragment} }`,
  SectionHero: `... on SectionHero { ${sectionHeroLightweightFragment} }`,
  SectionNavigation: `... on SectionNavigation { ${sectionNavigationLightweightFragment} }`,
  SectionBanner: `... on SectionBanner { ${sectionBannerLightweightFragment} }`,
  HubSpot: `... on HubSpot { ${hubSpotFragment} }`,
  ImpactHeadline: `... on ImpactHeadline { ${impactHeadlineFragment} }`,
  Launchpad: `... on Launchpad { ${launchpadFragment} }`,
  MapLocator: `... on MapLocator { ${mapLocatorFragment} }`,
  MediaCopy: `... on MediaCopy { ${mediaCopyFragment} }`,
  Nav: `... on Nav { ${mainNavFragment} }`,
  Note: `... on Note { ${noteFragment} }`,
  ResourcesItgBranded: `... on ResourcesItgBranded { ${resourcesItgBrandedFragment} }`,
  // Deprecating RichTextEditor - to be removed in a followup PR
  RichTextEditor: `... on RichTextEditor { ${richTextEditorFragment} }`,
  TextEditor: `... on TextEditor { ${richTextEditorFragment} bulletListStyle }`,
  SiteFooter: `... on SiteFooter { ${footerFragment} }`,
  SectionFooter: `... on SectionFooter { ${sectionFooterLightweightFragment} }`,
  SectionPageVisualSettings: `... on SectionPageVisualSettings { ${sectionPageVisualSettingsFragment} }`,
  StatsigVariantContainer: `... on StatsigVariantContainer { ${statsigExperimentFragment} }`,
  Subnav: `... on Subnav { ${subNavFragment} }`,
  TeaserLinks: `... on TeaserLinks { ${teaserlinksFragment} }`,
  Testimonial: `... on Testimonial { ${testimonialFragment} }`,
};
